import { ErrorSidePanel } from '@/components/errors';
import { useSendCode } from '@/hooks/api/contact-validation.hooks';
import { loadHistoryState } from '@/lib';
import { useForm } from '@deadcow-enterprises/react-forms';
import { ErrorCodes, logger, templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import { newContactStateSchema } from '../-model/flow';
import { type NewContactProps } from './-model/props';
import { NewContactLight } from './-skins';

export const Route = createFileRoute(
  '/(home)/home/(change-contacts)/_sheet/change-contacts/new-contact/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: loadHistoryState(newContactStateSchema, {
    throw: true,
    errorCode: ErrorCodes.AccessDenied,
  }),
});

function RouteComponent() {
  const { type } = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const isPhone = type === 'phone';

  const form = useForm<{ value: string }>(
    {
      value: {
        value: '',
        id: isPhone ? 'new-phone-number' : 'new-email',
        validator: isPhone
          ? z
              .string()
              .min(1, 'Home.ChangeContacts.NewContact.Errors.Required')
              .phone('Home.ChangeContacts.NewContact.Errors.InvalidPhone')
          : z
              .string()
              .min(1, 'Home.ChangeContacts.NewContact.Errors.Required')
              .emailAddress('Home.ChangeContacts.NewContact.Errors.InvalidEmail'),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { sendCodeAsync, isSending, captcha } = useSendCode();

  const handleSubmit = async ({ value }: typeof form.value) => {
    if (captcha.challenge && !captcha.token) return;

    await sendCodeAsync({
      targetId: value,
      type: isPhone ? 'Sms' : 'Email',
      captcha,
    });

    navigate({
      to: '/home/change-contacts/code-input',
      state: { type, contact: value },
    });
  };

  const props: NewContactProps = {
    isPhone,
    form,
    isSubmitting: isSending,
    captcha,
    onSubmit: handleSubmit,
  };
  return templates.getSkinSelector(skinConfig(props));
}

export function skinConfig(props: NewContactProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <NewContactLight {...props} />,
  };
}
